// "Ganze Notiz" bzw. "Ganzer Eintrag": im Überblick ist ein Eintrag höchstens drei
// Zeilen hoch. Ist er gekürzt, öffnet der Knopf eine Seitenleiste rechts (wie
// Dynamics), schmal ein Vollbild. Fokus auf den Titel, Esc und "Schließen"
// schließen, der Fokus geht an den Knopf zurück. Im Vollbild schließt die
// Zurück-Geste die Leiste, statt die Seite zu verlassen -- dasselbe Muster wie die
// Befehlspalette. Entscheidungen in ST-ACT-001.

import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  makeStyles,
  OverlayDrawer,
  tokens,
} from '@fluentui/react-components';

import { Symbol } from '../../shell/symbole.tsx';
import { useZurueckSchliesstVollbild } from '../../shell/zurueck-schliesst-vollbild.ts';

const useStyles = makeStyles({
  knopf: { alignSelf: 'flex-start', minWidth: 0, paddingLeft: 0, paddingRight: 0, color: tokens.colorBrandForeground1 },
  text: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  meta: { display: 'block', color: tokens.colorNeutralForeground2, marginBottom: '0.75rem' },
  // Vollbild heisst bei Fluent 100vw und schliesst den Scrollbalken ein; 100 % ist das Fenster ohne ihn.
  vollbild: { width: '100%', maxWidth: '100%' },
  // Der Textbereich scrollt per Tastatur: Tab-Halt mit Namen und sichtbarem Ring (WCAG 2.1.1).
  koerper: { ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '-2px' } },
  titel: { ':focus': { outline: 'none' }, ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, borderRadius: tokens.borderRadiusMedium } },
});

/** Ob der Text im Element `id` abgeschnitten ist; misst neu, wenn sich die Grösse ändert. */
export function useGekuerzt(id: string, aktiv: boolean): boolean {
  const [gekuerzt, setGekuerzt] = useState(false);
  useEffect(() => {
    const el = document.getElementById(id);
    if (!aktiv || el === null) {
      setGekuerzt(false);
      return undefined;
    }
    const messen = (): void => setGekuerzt(el.scrollHeight > el.clientHeight + 1);
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [id, aktiv]);
  return gekuerzt;
}

const BREIT = '(min-width: 1024px)';
/** Das Gegenstück zu `useBreit`: dort ist die Leiste ein Vollbild. */
const VOLLBILD_NOTIZ = `not all and ${BREIT}`;

function useBreit(): boolean {
  const abfrage = BREIT;
  const [breit, setBreit] = useState(() => window.matchMedia(abfrage).matches);
  useEffect(() => {
    const liste = window.matchMedia(abfrage);
    const wechsel = (): void => setBreit(liste.matches);
    liste.addEventListener('change', wechsel);
    return () => liste.removeEventListener('change', wechsel);
  }, []);
  return breit;
}

export function GanzeNotiz({
  bezug,
  meta,
  text,
  knopfId,
  notiz,
  stelle,
}: {
  /** "Notiz vom 10.09.2026, 16:00": Titel der Leiste und Teil des Knopfnamens. */
  readonly bezug: string;
  /** Autor, Zeit und Quelle wie am Eintrag. */
  readonly meta: string;
  readonly text: string;
  /** Die Kennung des Knopfs; dorthin kehrt der Fokus nach dem Schließen zurück. */
  readonly knopfId: string;
  /** Eine Notiz heißt "Ganze Notiz", jede andere Art "Ganzer Eintrag". */
  readonly notiz: boolean;
  /** Bei gleicher Art und Minute die Stelle unter Gleichen ("1. von 2"), damit die Namen sich unterscheiden. */
  readonly stelle?: string | undefined;
}): ReactElement {
  const wort = notiz ? 'Ganze Notiz' : 'Ganzer Eintrag';
  const name = stelle === undefined ? `${wort}, ${bezug}` : `${wort}, ${bezug}, ${stelle}`;
  const own = useStyles();
  const breit = useBreit();
  const [offen, setOffen] = useState(false);
  const titelRef = useRef<HTMLHeadingElement | null>(null);
  const warOffen = useRef(false);
  const titelId = `${knopfId}-titel`;
  // Unter 1024 ist die Leiste ein Vollbild; die Zurück-Geste schließt sie.
  const geste = useZurueckSchliesstVollbild(() => setOffen(false), VOLLBILD_NOTIZ);
  const oeffnen = (): void => {
    geste.eintragen();
    setOffen(true);
  };
  const schliessen = (): void => {
    setOffen(false);
    geste.zuruecknehmen();
  };
  useEffect(() => {
    if (offen) {
      warOffen.current = true;
      // Fluent setzt den Anfangsfokus der Leiste selbst auf den ersten Knopf; der Titel
      // bekommt ihn danach, damit die Leiste mit ihrem Namen beginnt.
      let zweiter = 0;
      const erster = window.requestAnimationFrame(() => {
        zweiter = window.requestAnimationFrame(() => titelRef.current?.focus());
      });
      return () => {
        window.cancelAnimationFrame(erster);
        window.cancelAnimationFrame(zweiter);
      };
    } else if (warOffen.current) {
      warOffen.current = false;
      document.getElementById(knopfId)?.focus();
    }
    return undefined;
  }, [offen, knopfId]);

  return (
    <>
      <Button id={knopfId} appearance="transparent" size="small" className={own.knopf} aria-label={name} onClick={oeffnen}>
        {wort}
      </Button>
      {offen ? (
        <OverlayDrawer open position="end" size={breit ? 'medium' : 'full'} className={breit ? undefined : own.vollbild} onOpenChange={(_, daten) => (daten.open ? setOffen(true) : schliessen())}>
          <DrawerHeader>
            <DrawerHeaderTitle
              heading={{ as: 'h2', ref: titelRef, id: titelId, tabIndex: -1, className: own.titel }}
              action={
                <Button appearance="subtle" aria-label="Schließen" icon={<Symbol name="schliessen" />} onClick={schliessen} />
              }
            >
              {bezug}
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody tabIndex={0} role="region" aria-labelledby={titelId} className={own.koerper}>
            <Caption1 className={own.meta}>{meta}</Caption1>
            <Body1 className={own.text}>{text}</Body1>
          </DrawerBody>
        </OverlayDrawer>
      ) : undefined}
    </>
  );
}
